// Detection Dashboard — top-N aggregations across the projects
// the caller can see. Three cards: by CVE, by project, by package.
// Each card surfaces the per-severity breakdown so an operator
// can quickly tell whether the burden is critical/high (act now)
// or low/medium (queue for the next sprint).
//
// All three SWRs revalidate once a minute; in steady state the
// numbers move slowly enough that minute-granularity is fine and
// bigger DBs don't get hammered with COUNT(*) every reload.

import { Link } from 'react-router-dom';
import { ScanLine, AlertTriangle, FolderTree, Package2 } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import {
  useStatsByCVE,
  useStatsByProject,
  useStatsByPackage,
  type StatsRow,
} from '../../api/detection';

export function DetectionDashboard() {
  const cve = useStatsByCVE();
  const project = useStatsByProject();
  const pkg = useStatsByPackage();

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <p className="orb-eyebrow text-accent/80 mb-2">Detection</p>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
              <ScanLine className="h-5 w-5 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-semibold text-fg leading-tight">Dashboard</h1>
              <p className="text-fg-muted text-sm mt-2 max-w-2xl">
                Open findings rolled up across the projects you can see.
                Top 25 per group, refreshed every minute.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <StatsCard
            title="Top CVEs"
            description="Open findings by rule ID (CVE/GHSA/orbital). Click a row to filter findings."
            icon={<AlertTriangle className="h-5 w-5 text-danger" />}
            rows={cve.data ?? []}
            isLoading={cve.isLoading}
            renderKey={(r) => (
              <Link
                to={`/detection/findings?status=open&search=${encodeURIComponent(r.key)}`}
                className="font-mono text-xs text-accent hover:underline"
              >
                {r.key}
              </Link>
            )}
          />
          <StatsCard
            title="Top projects"
            description="Open findings grouped by project."
            icon={<FolderTree className="h-5 w-5 text-nebula" />}
            rows={project.data ?? []}
            isLoading={project.isLoading}
            renderKey={(r) => (
              <Link
                to={`/detection/findings?status=open&project=${encodeURIComponent(r.key)}`}
                className="font-mono text-xs text-accent hover:underline"
              >
                {r.label || r.key}
              </Link>
            )}
          />
          <StatsCard
            title="Top packages"
            description="Open findings grouped by package coordinate."
            icon={<Package2 className="h-5 w-5 text-accent" />}
            rows={pkg.data ?? []}
            isLoading={pkg.isLoading}
            renderKey={(r) => (
              <span className="font-mono text-xs text-fg break-all">
                {r.key}
                {r.label && (
                  <span className="text-fg-subtle ml-2 text-[10px]">{r.label}</span>
                )}
              </span>
            )}
          />
        </div>
      </div>
    </AppShell>
  );
}

function StatsCard({
  title,
  description,
  icon,
  rows,
  isLoading,
  renderKey,
}: Readonly<{
  title: string;
  description: string;
  icon: React.ReactNode;
  rows: StatsRow[];
  isLoading: boolean;
  renderKey: (r: StatsRow) => React.ReactNode;
}>) {
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {icon}
            {title}
          </span>
        }
        description={description}
        actions={isLoading ? <Spinner size="xs" /> : null}
      />
      <CardBody>
        {rows.length === 0 ? (
          <p className="text-xs text-fg-muted italic text-center py-6">
            No open findings.
          </p>
        ) : (
          <ul className="space-y-2 max-h-[32rem] overflow-y-auto">
            {rows.map((r) => (
              <li
                key={r.key}
                className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-bg-hover/40"
              >
                <SeverityBar row={r} />
                <span className="flex-1 min-w-0 break-all">{renderKey(r)}</span>
                <span className="text-xs text-fg tabular-nums shrink-0">{r.total}</span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

// SeverityBar renders a tiny stacked bar showing the severity
// breakdown for one row. Visually quick to scan: a row dominated by
// critical/high reads as red, mostly-medium reads as amber.
function SeverityBar({ row }: Readonly<{ row: StatsRow }>) {
  const total = row.total || 1;
  const widths = [
    { key: 'crit', n: row.critical, cls: 'bg-danger' },
    { key: 'high', n: row.high, cls: 'bg-danger/60' },
    { key: 'med', n: row.medium, cls: 'bg-warning' },
    { key: 'low', n: row.low, cls: 'bg-accent' },
    { key: 'info', n: row.info, cls: 'bg-fg-muted/40' },
  ];
  const tooltip = `critical ${row.critical} · high ${row.high} · medium ${row.medium} · low ${row.low} · info ${row.info}`;
  return (
    <div className="h-3 w-24 flex rounded overflow-hidden border border-border" title={tooltip}>
      {widths.map((w) => (
        w.n > 0 && (
          <div
            key={w.key}
            className={w.cls}
            style={{ width: `${(w.n / total) * 100}%` }}
          />
        )
      ))}
    </div>
  );
}

